import apiClient, { type ApiError, getAuthToken } from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { API_ENDPOINTS } from '@/lib/api/config'
import { loadRolesConfig } from '@/features/auth/services/rolesConfigApi'
import { env } from '@/config/env'
import type {
  UserFilters,
  UserListResponse,
  UserDetail,
  EmailCheckResult,
  CreateUserRequest,
  UpdateUserRequest,
  AssignRoleRequest,
  SetFinanceManagerRequest,
  CompanyLookup,
  PropertyLookup,
  OrganizationLookup,
  NotificationItem,
  NotificationDetail,
  NotificationCounts,
} from '../types'
import { stripNonAscii } from '../utils/userManagementFormatters'

// ─── Users ────────────────────────────────────────────────────────────────────

export async function getUsers(filters: UserFilters): Promise<UserListResponse> {
  try {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.perPage) params.append('perPage', String(filters.perPage))
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.orderBy) params.append('orderBy', filters.orderBy)
    if (filters.organization_type) params.append('organization_type', filters.organization_type)
    if (filters.company_id) params.append('company_id', filters.company_id)
    if (filters.property_id) params.append('property_id', filters.property_id)
    if (filters.listed !== undefined && filters.listed !== '') params.append('listed', filters.listed)
    if (filters.account_status) params.append('account_status', filters.account_status)

    // Non-ASCII stripped (match Angular). URLSearchParams handles encoding (avoid double-encoding).
    if (filters.username) {
      const cleaned = stripNonAscii(filters.username)
      if (cleaned) params.append('username', cleaned)
    }

    const response = await apiClient.get<UserListResponse>(
      `${API_ENDPOINTS.USER_LIST}?${params.toString()}`
    )
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch users.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function getUserDetail(userId: string | number): Promise<UserDetail> {
  try {
    const response = await apiClient.get<UserDetail>(API_ENDPOINTS.USER_DETAIL(userId))
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch user details.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function createUser(data: CreateUserRequest): Promise<number> {
  try {
    const response = await apiClient.post<number | string | { user_id?: number }>(
      API_ENDPOINTS.USER_LIST,
      data
    )
    const raw = response.data
    if (typeof raw === 'number') return raw
    if (typeof raw === 'string') return Number(raw)
    return Number((raw as { user_id?: number }).user_id)
  } catch (error) {
    const apiError = error as ApiError & { data?: Record<string, unknown> }
    throw {
      message: extractErrorMessage(error, 'Failed to create user.'),
      status: apiError.status,
      error: apiError.data || apiError.errors,
    }
  }
}

export async function updateUser(userId: string | number, data: UpdateUserRequest): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.USER_UPDATE(userId), data)
  } catch (error) {
    const apiError = error as ApiError & { data?: Record<string, unknown> }
    throw {
      message: extractErrorMessage(error, 'Failed to update user.'),
      status: apiError.status,
      error: apiError.data || apiError.errors,
    }
  }
}

export async function disableUser(userId: string | number): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.USER_DISABLE(userId))
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to disable user.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function deleteUser(userId: string | number): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.USER_DELETE_HARD(userId))
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to delete user.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function bulkDeleteUsers(userIds: number[]): Promise<string> {
  try {
    const response = await apiClient.post<string>(API_ENDPOINTS.USER_BULK_DELETE, {
      user_ids: userIds,
    })
    return typeof response.data === 'string' ? response.data : 'Users deleted successfully.'
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to bulk delete users.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function enableUser(userId: string | number): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.USER_ENABLE(userId))
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to re-enable user.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

// ─── Email ────────────────────────────────────────────────────────────────────

export async function checkEmail(email: string): Promise<EmailCheckResult | null> {
  try {
    const response = await apiClient.get<EmailCheckResult>(
      `${API_ENDPOINTS.USER_EMAIL_CHECK}?email=${encodeURIComponent(email)}`
    )
    return response.data
  } catch (error) {
    if ((error as ApiError).status === 404) return null
    throw {
      message: extractErrorMessage(error, 'Failed to check email.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function verifyEmail(userId: string | number): Promise<string> {
  try {
    const response = await apiClient.get<unknown>(API_ENDPOINTS.USER_EMAIL_VERIFY(userId))
    const raw = response.data
    if (typeof raw === 'string') return raw
    if (raw && typeof raw === 'object') {
      const obj = raw as Record<string, unknown>
      const val = obj.email_verify ?? obj.status ?? obj.data ?? obj.message ?? obj.email_verify_status
      if (typeof val === 'string') return val
    }
    return String(raw ?? '')
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to verify email.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

// ─── Password ────────────────────────────────────────────────────────────────

export async function setTempPassword(userId: string | number, tempPassword: string): Promise<string> {
  try {
    const res = await apiClient.post<string>(API_ENDPOINTS.USER_TEMP_PASSWORD(userId), {
      temp_password: tempPassword,
    })
    return typeof res.data === 'string' ? res.data : 'Temp Password Set Successfully!'
  } catch (error) {
    const apiError = error as ApiError & { data?: Record<string, unknown> }
    throw {
      message: extractErrorMessage(error, 'Failed to set temp password.'),
      status: apiError.status,
      error: apiError.data || apiError.errors,
    }
  }
}

export async function resetPassword(email: string): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.FORGOT_PASSWORD, { email })
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to send password reset.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

// ─── Roles ────────────────────────────────────────────────────────────────────

export async function assignRole(
  userId: string | number,
  data: AssignRoleRequest
): Promise<string> {
  try {
    const response = await apiClient.post<string>(API_ENDPOINTS.USER_ROLES_ASSIGN(userId), data)
    return typeof response.data === 'string' ? response.data : 'Role assigned successfully.'
  } catch (error) {
    const apiError = error as ApiError & { data?: Record<string, unknown> }
    throw {
      message: extractErrorMessage(error, 'Failed to assign role.'),
      status: apiError.status,
      error: apiError.data || apiError.errors,
    }
  }
}

export async function updateRole(
  userId: string | number,
  roleId: string | number,
  data: AssignRoleRequest
): Promise<string> {
  try {
    const response = await apiClient.post<string>(API_ENDPOINTS.USER_ROLE_UPDATE(userId, roleId), data)
    return typeof response.data === 'string' ? response.data : 'Role updated successfully.'
  } catch (error) {
    const apiError = error as ApiError & { data?: Record<string, unknown> }
    throw {
      message: extractErrorMessage(error, 'Failed to update role.'),
      status: apiError.status,
      error: apiError.data || apiError.errors,
    }
  }
}

export async function removeRole(userId: string | number, roleId: string | number): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.USER_ROLE_REMOVE(userId, roleId))
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to remove role.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function setFinanceManager(
  userId: string | number,
  data: SetFinanceManagerRequest
): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.USER_FINANCE_MANAGER(userId), data)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to update finance manager.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function updateSalesRole(
  userId: string | number,
  salesRole: string
): Promise<string> {
  try {
    const response = await apiClient.post<string>(API_ENDPOINTS.USER_SALES_ROLE(userId), {
      sales_role: salesRole,
    })
    return typeof response.data === 'string' ? response.data : ''
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to update sales role.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

// ─── Export ───────────────────────────────────────────────────────────────────

export function getUserExportUrl(filters: UserFilters): string {
  const token = getAuthToken() ?? ''
  const params = new URLSearchParams()
  if (filters.organization_type) params.append('organization_type', filters.organization_type)
  if (filters.company_id) params.append('company_id', filters.company_id)
  if (filters.property_id) params.append('property_id', filters.property_id)
  if (filters.listed !== undefined && filters.listed !== '') params.append('listed', filters.listed)
  if (filters.account_status) params.append('account_status', filters.account_status)
  if (filters.username) params.append('username', stripNonAscii(filters.username))
  // Angular export() loops all urlParams, so page/perPage/sortBy/orderBy are included too.
  if (filters.page) params.append('page', String(filters.page))
  if (filters.perPage) params.append('perPage', String(filters.perPage))
  if (filters.sortBy) params.append('sortBy', filters.sortBy)
  if (filters.orderBy) params.append('orderBy', filters.orderBy)
  params.append('access_token', token)
  params.append('view', 'xlsx')

  const base = import.meta.env.DEV ? '' : (env.apiBaseUrl ?? '')
  return `${base}${API_ENDPOINTS.USER_EXPORT}?${params.toString()}`
}

// ─── Lookups ──────────────────────────────────────────────────────────────────

export async function getCompanies(q?: string, listed: string | number = 'all'): Promise<CompanyLookup[]> {
  try {
    const params = new URLSearchParams()
    if (q?.trim()) params.append('q', q.trim())
    params.append('listed', String(listed))
    params.append('page', 'show_listed')
    const response = await apiClient.get<CompanyLookup[] | { data?: CompanyLookup[] }>(
      `${API_ENDPOINTS.ALL_COMPANIES}?${params.toString()}`
    )
    const raw = response.data
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: CompanyLookup[] }).data)) {
      return (raw as { data: CompanyLookup[] }).data
    }
    return []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch companies.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function getProperties(
  q?: string,
  companyId?: string | number,
  listed: string | number = 'all'
): Promise<PropertyLookup[]> {
  try {
    const params = new URLSearchParams()
    if (q?.trim()) params.append('q', q.trim())
    if (companyId) params.append('company_id', String(companyId))
    params.append('listed', String(listed))
    params.append('page', 'show_listed')
    const response = await apiClient.get<PropertyLookup[] | { data?: PropertyLookup[] }>(
      `${API_ENDPOINTS.ALL_PROPERTY}?${params.toString()}`
    )
    const raw = response.data
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: PropertyLookup[] }).data)) {
      return (raw as { data: PropertyLookup[] }).data
    }
    return []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch properties.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function getOrganizations(q?: string): Promise<OrganizationLookup[]> {
  try {
    const params = new URLSearchParams()
    if (q?.trim()) params.append('q', q.trim())
    const response = await apiClient.get<OrganizationLookup[] | { data?: OrganizationLookup[] }>(
      `${API_ENDPOINTS.ALL_ORGANIZATIONS}?${params.toString()}`
    )
    const raw = response.data
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: OrganizationLookup[] }).data)) {
      return (raw as { data: OrganizationLookup[] }).data
    }
    return []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch organizations.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

// ─── Notifications ────────────────────────────────────────────────────────────

export async function getNotifications(params: Record<string, string | number>): Promise<{
  data: NotificationItem[]
  total: number
  current: number
  last: number
}> {
  try {
    const urlParams = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => urlParams.append(k, String(v)))
    const response = await apiClient.get<{
      data: NotificationItem[]
      total: number
      current: number
      last: number
    }>(`${API_ENDPOINTS.USER_NOTIFICATION}?${urlParams.toString()}`)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch notifications.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function getNotificationDetail(notificationId: string | number): Promise<NotificationDetail> {
  try {
    const response = await apiClient.get<NotificationDetail>(
      API_ENDPOINTS.USER_NOTIFICATION_DETAIL(notificationId)
    )
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch notification detail.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function updateNotification(
  notificationId: string | number,
  data: Record<string, number>
): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.USER_NOTIFICATION_DETAIL(notificationId), data)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to update notification.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function bulkDeleteNotifications(
  userId: string | number,
  notificationIds: number[]
): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.USER_NOTIFICATION_MULTI_DELETE(userId), {
      notification_ids: notificationIds,
      deleted: 1,
    })
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to delete notifications.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function getNotificationCounts(): Promise<NotificationCounts> {
  try {
    const response = await apiClient.get<NotificationCounts>(API_ENDPOINTS.USER_NOTIFICATION_COUNT)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch notification counts.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

// ─── Roles Config ─────────────────────────────────────────────────────────────

export async function getRolesConfig(): Promise<Record<string, import('../types').RoleCategory>> {
  try {
    return (await loadRolesConfig()) as Record<string, import('../types').RoleCategory>
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch roles configuration.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}
